import { PeriodSelector } from "@/components/school-head/PeriodSelector";
import { useScopedVisits } from "@/hooks/useSchoolHeadData";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format, parseISO, startOfWeek, addDays } from "date-fns";

export default function SchoolHeadReports() {
  const { data: visits = [], isLoading } = useScopedVisits();

  // Weekly aggregation Sun–Sat
  const weekMap = new Map<string, { week: string; total: number; student: number; staff: number; followUp: number }>();
  for (const v of visits) {
    if (!v.date) continue;
    const d = parseISO(v.date);
    const wk = startOfWeek(d, { weekStartsOn: 0 });
    const key = format(wk, "yyyy-MM-dd");
    const label = `${format(wk, "MMM d")} – ${format(addDays(wk, 6), "MMM d")}`;
    const row = weekMap.get(key) || { week: label, total: 0, student: 0, staff: 0, followUp: 0 };
    row.total += 1;
    if (v.student_id) row.student += 1;
    if (v.community_member_id) row.staff += 1;
    if (v.requires_followup) row.followUp += 1;
    weekMap.set(key, row);
  }
  const weekly = Array.from(weekMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, v]) => v);

  const totals = {
    total: visits.length,
    student: visits.filter((v) => v.student_id).length,
    staff: visits.filter((v) => v.community_member_id).length,
    followUp: visits.filter((v) => v.requires_followup).length,
  };

  return (
    <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Reports</h1>
          <p className="text-sm text-muted-foreground">
            Summarised activity for the selected period. Weeks run Sunday–Saturday.
          </p>
        </div>

        <PeriodSelector />

        <div className="grid gap-3 sm:grid-cols-4">
          <SummaryTile label="Total visits" value={totals.total} />
          <SummaryTile label="Student visits" value={totals.student} />
          <SummaryTile label="Staff visits" value={totals.staff} />
          <SummaryTile label="Follow-ups" value={totals.followUp} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Weekly breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : weekly.length === 0 ? (
              <p className="text-sm text-muted-foreground">No visits recorded in this period.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-4">Week</th>
                      <th className="py-2 pr-4">Total</th>
                      <th className="py-2 pr-4">Students</th>
                      <th className="py-2 pr-4">Staff</th>
                      <th className="py-2 pr-4">Follow-ups</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekly.map((w) => (
                      <tr key={w.week} className="border-t">
                        <td className="py-2 pr-4 font-medium">{w.week}</td>
                        <td className="py-2 pr-4">{w.total}</td>
                        <td className="py-2 pr-4">{w.student}</td>
                        <td className="py-2 pr-4">{w.staff}</td>
                        <td className="py-2 pr-4">{w.followUp}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
  );
}

function SummaryTile({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold">{value.toLocaleString()}</div>
      </CardContent>
    </Card>
  );
}
